import { 
  getStringAnchorProperties,
  removeLoadSpinner,
  ISettings,
  IDataItem
} from './rms-ui.navi';

/** 
 * ======================================================================== 
 * RMS Header
 * ======================================================================== 
 */
/** Initialize Header module (add default classes) */
export function initHeaderModule(settings: ISettings, target: string) {
  const { classNames } = settings.header;
  const header = document.querySelector(target as string);
  for (const className of classNames) if (!header.classList.contains(className)) header.classList.add(className);
}

/** Render Header */
export function renderHeader(settings: ISettings, data: IHeaderNavigation) {
  const { target, targetGroup } = settings.header;
  const header = document.querySelector(target as string);
  try {
    removeLoadSpinner(settings, header);
    if (!data) return;
    let groupContent = header?.querySelector(targetGroup);
    if (!groupContent) {
      groupContent = document.createElement('div');
      groupContent.className = targetGroup.substring(1, targetGroup.length);
      header?.appendChild(groupContent);
    }
    const helpContent = renderHeaderHelp(settings, data.faqInfo);
    if (helpContent) groupContent.appendChild(helpContent);
    const eccContent = renderHeaderECC(settings, data.eccInfo);
    if (eccContent) groupContent.appendChild(eccContent);
    const shopContent = renderHeaderShop(settings, data.shopInfo);
    if (shopContent) groupContent.appendChild(shopContent);
    attachHeaderHandler(settings);
  } catch (error) {
    throw error;
  }
}

/** Render Header Help section */
function renderHeaderHelp(settings: ISettings, helpData?: IHeaderFaqInfo): HTMLDivElement | undefined {
  if (!helpData) return;
  const { classGroupHelp, classPopover, classPopoverBody, classPopoverButton } = settings.header as IHeader;
  if (!helpData.segments) return;
  const helpContent = document.createElement('div');
  helpContent.className = `${classGroupHelp} ${classPopover}`;
  helpContent.id = helpData.id;
  helpContent.dataset.ratid = helpData.id;
  helpContent.dataset.ratevent = 'click';
  helpContent.dataset.ratparam = 'all';
  helpContent.setAttribute('triforcev2status', 'done');
  const popoverBtn = document.createElement('div');
  popoverBtn.className = classPopoverButton;
  const popoverBody = document.createElement('div');
  popoverBody.className = classPopoverBody;
  renderHeaderHelpContent(settings, helpData, popoverBody);
  helpContent.appendChild(popoverBtn);
  helpContent.appendChild(popoverBody);
  return helpContent;
}

/** Render Header Help section's content */
function renderHeaderHelpContent(settings: ISettings, helpData: IHeaderFaqInfo, parentElement: HTMLDivElement) {
  const { classPopoverContent, classPopoverContentItem } = settings.header as IHeader;
  if (!helpData.segments?.length) return;
  helpData.segments.map(contentItem => {
    let labelContent = '';
    const popoverContent = document.createElement('div');
    popoverContent.className = classPopoverContent;
    if (contentItem.title?.length) labelContent = `<h1>${contentItem.title.join('<br/>')}</h1>`;
    if (!contentItem.links?.length) return;
    const itemsContent = contentItem.links.map(item => {
      if (!item.canAccess) return '';
      const title = item.title?.length ? item.title.join('<br/>') : '';
      const itemContent = item.canAccess ? item.url ? `<a ${getStringAnchorProperties(item)}>${title}</a>` : `<span id="${item.id}">${title}</span>` : '';
      return `<div class="${classPopoverContentItem}">${itemContent}</div>`;
    }).join('');
    popoverContent.innerHTML = `${labelContent}${itemsContent}`;
    parentElement.appendChild(popoverContent);
  });
}

/** Render Header ECC section */
function renderHeaderECC(settings: ISettings, eccData?: IHeaderEccInfo): HTMLDivElement | undefined {
  if (!eccData) return;
  const { classGroupEcc, classPopover, classPopoverBody, classPopoverButton } = settings.header as IHeader;
  const eccContent = document.createElement('div');
  eccContent.className = `${classGroupEcc} ${classPopover}`;
  eccContent.id = eccData.id;
  eccContent.dataset.ratid = eccData.id;
  eccContent.dataset.ratevent = 'click';
  eccContent.dataset.ratparam = 'all';
  eccContent.setAttribute('triforcev2status', 'done');
  const popoverBtn = document.createElement('div');
  popoverBtn.className = classPopoverButton;
  const popoverBody = document.createElement('div');
  popoverBody.className = classPopoverBody;
  renderHeaderECCContent(settings, eccData, popoverBody);
  eccContent.appendChild(popoverBtn);
  eccContent.appendChild(popoverBody);
  return eccContent;
}

/** Render Header ECC section's content */
function renderHeaderECCContent(settings: ISettings, eccData: IHeaderEccInfo, parentElement: HTMLDivElement) {
  const { 
    classPopoverContent,
    classColumns,
    classColumnsCol,
    classColumnsColAuto,
    classGroupEccImg,
    classMarginBottom,
    classMarginLeft,
    labelGroupEccTelephone
  } = settings.header as IHeader;
  if (!eccData) return;
  const { title, groupName, telephone } = eccData;
  const popoverContent = document.createElement('div');
  popoverContent.className = classPopoverContent;
  const titleContent = title ? `<div class="${classColumns} ${classMarginBottom}"><span>${title.join('<br/>')}</span></div>` : '';
  const image = eccData.image;
  const name = eccData.name;
  const inquire = eccData.inquire;
  const eccContent = ((image && image.link && image.link.canAccess) || (name && name.canAccess)) ? `
    <div class="${classColumns} ${classMarginBottom}">
      <div class="${classColumnsColAuto} ${classGroupEccImg}">
        <a ${getStringAnchorProperties(image.link)}}>
          <img src="${image.url}" alt="Ecc Image"/>
        </a>
      </div>
      <div class="${classColumnsCol} ${classMarginLeft}">
        <div class="${classColumns}">
          <div class="${classColumnsCol}">${groupName}</div>
        </div>
        <div class="${classColumns}">
          <div class="${classColumnsCol}">
            <a ${getStringAnchorProperties(name)}>${name.title && name.title.join(' ')}</a>
          </div>
        </div>
      </div>
    </div>
  ` : '';
  const telephoneContent = `
    <div class="${classColumns} ${classMarginBottom}">
      <div class="${classColumnsColAuto}">${labelGroupEccTelephone}</div>
      <div class="${classColumns}">${telephone}</div>
    </div>
  `;
  const inquireContent = inquire.canAccess ? `<div class="${classColumns} ${classMarginBottom}"><a ${getStringAnchorProperties(inquire)}>${inquire.title && inquire.title.join('<br/>')}</a></div>` : '';
  popoverContent.innerHTML = `${titleContent}${eccContent}${telephoneContent}${inquireContent}`;
  parentElement.appendChild(popoverContent);
}

/** Render Header Shop section */
function renderHeaderShop(settings: ISettings, shopData?: IHeaderShopInfo): HTMLDivElement | undefined {
  if (!shopData) return;
  const { classGroupShop, classPopover, classPopoverBody, classPopoverButton } = settings.header as IHeader;
  const shopContent = document.createElement('div');
  shopContent.className = `${classGroupShop} ${classPopover}`;
  shopContent.id = shopData.id;
  shopContent.dataset.ratid = shopData.id;
  shopContent.dataset.ratevent = 'click';
  shopContent.dataset.ratparam = 'all';
  shopContent.setAttribute('triforcev2status', 'done');
  const popoverBtn = document.createElement('div');
  popoverBtn.className = classPopoverButton;
  const popoverBody = document.createElement('div');
  popoverBody.className = classPopoverBody;
  renderHeaderShopButton(settings, shopData, popoverBtn);
  renderHeaderShopContent(settings, shopData, popoverBody);
  shopContent.appendChild(popoverBtn);
  shopContent.appendChild(popoverBody);
  return shopContent;
}

/** Render Header Shop section's button */
function renderHeaderShopButton(settings: ISettings, shopData: IHeaderShopInfo, parentElement: HTMLDivElement) {
  const { classGroupShopImg, classGroupShopName, classGroupShopIcon } = settings.header as IHeader;
  const img = document.createElement('figure');
  img.className = classGroupShopImg;
  if (shopData.imageUrl) img.innerHTML = `<img src="${shopData.imageUrl}" alt="Shop Image" />`;
  const name = document.createElement('div');
  name.className = classGroupShopName;
  if (shopData.title) name.innerHTML = shopData.title.join('<br/>');
  const icon = document.createElement('i');
  icon.className = classGroupShopIcon;
  parentElement.appendChild(img);
  parentElement.appendChild(name);
  parentElement.appendChild(icon);
}

/** Render Header Shop section's content */
function renderHeaderShopContent(settings: ISettings, shopData: IHeaderShopInfo, parentElement: HTMLDivElement) {
  const {
    classPopoverContent,
    classColumns,
    classColumnsCol,
    classColumnsColAuto,
    classMarginBottom,
    classGroupShopPageButton,
    labelGroupShopURL,
    labelGroupShopStatus,
    labelGroupShopPlan,
  } = settings.header as IHeader;
  const { shopName, shopNameUrl, shopStatus, shopPlan, shopUrl, segments } = shopData;
  const shopInfoSegment = document.createElement('div');
  shopInfoSegment.className = classPopoverContent;
  const shopInfoTitleContent = shopName ? `<div class="${classColumns} ${classMarginBottom}">${shopName}</div>` : '';
  const shopInfoURLContent = shopNameUrl ? `
    <div class="${classColumns} ${classMarginBottom}">
      <div class="${classColumnsColAuto}">${labelGroupShopURL}</div>
      <div class="${classColumnsCol}">${shopNameUrl}</div>
    </div>
  ` : '';
  const shopStatusContent = shopStatus && shopStatus.title && shopStatus.link ? `
    <div class="${classColumns} ${classMarginBottom}">
      <div class="${classColumnsColAuto}">${labelGroupShopStatus}</div>
      <div class="${classColumnsCol}">
        <div class="${classColumns}">
          <div class="${classColumnsCol}">${shopStatus.title.join('<br/>')}</div>
        </div>
        <div class="${classColumns}">
          <div class="${classColumnsCol}">
            （<a ${getStringAnchorProperties(shopStatus.link)}>${shopStatus.link.title.join('<br/>')}</a>）
          </div>
        </div>
      </div>
    </div>
  ` : '';
  const shopPlanContent = shopPlan && shopPlan.title && shopPlan.link ? `
    <div class="${classColumns} ${classMarginBottom}">
      <div class="${classColumnsColAuto}">${labelGroupShopPlan}</div>
      <div class="${classColumnsCol}">
        <div class="${classColumns}">
          <div class="${classColumnsCol}">${shopPlan.title[0]}</div>
        </div>
        <div class="${classColumns}">
          <div class="${classColumnsCol}">
            （<a ${getStringAnchorProperties(shopPlan.link)}>${shopPlan.link.title.join('<br/>')}</a>）
          </div>
        </div>
      </div>
    </div>
  ` : '';
  const shopPageButtonContent = shopUrl ? `<a class="${classGroupShopPageButton}" ${getStringAnchorProperties(shopUrl)}>${shopUrl.title.join('<br/>')}</a>` : '';

  shopInfoSegment.innerHTML = `
    ${shopInfoTitleContent}
    ${shopInfoURLContent}
    ${shopStatusContent}
    ${shopPlanContent}
    ${shopPageButtonContent}
  `;

  parentElement.append(shopInfoSegment);

  if (!segments.length) return;
  for (const segment of segments) {
    if (!segment.links.length) break;
    const segmentContent = document.createElement('div');
    segmentContent.className = classPopoverContent;
    for (let index = 0; index < segment.links.length; index++) {
      const link = segment.links[index];
      segmentContent.innerHTML = segmentContent.innerHTML + `
        <div class="${classColumns} ${index +1 !== segment.links.length ? classMarginBottom : ''}">
          <div class="${classColumnsCol}">
            <a ${getStringAnchorProperties(link)}>${link.title.join('<br/>')}</a>
          </div>
        </div>
      `;
    }
    parentElement.append(segmentContent);
  }
}

/** Handle active class on popovers */
const attachHeaderHandler = (settings: ISettings) => {
  const { target, classPopover, classActive, classPopoverButton } = settings.header as IHeader;
  const popoverButtons = document.querySelectorAll(`${target} .${classPopover} .${classPopoverButton}`);
  popoverButtons.forEach(pob => pob.addEventListener('click', () => {
    const popovers = document.querySelectorAll(`${target} .${classPopover}.${classActive}`);
    const pn = pob.parentNode;
    const filteredPopovers: Element[] = [];
    popovers.forEach(po => {
      if (po !== pn) filteredPopovers.push(po);
    });
    filteredPopovers.map(fpo => (fpo as HTMLElement).classList.remove(classActive));
    (pn as HTMLElement).classList.toggle(classActive);
  }));
  document.onclick = (event) => {
    const parent = (event.target as HTMLElement).closest(`.${classPopover}.${classActive}`);
    if (parent) return;
    popoverButtons.forEach(pob => (pob.parentNode as HTMLElement).classList.remove(classActive));
  };
};

export interface IHeader {
  target: string;
  targetGroup: string;
  classNames: string[];
  classLoadSpinner: string;
  classGroupHelp: string;
  classGroupEcc: string;
  classGroupShop: string;
  classGroupShopPageButton: string;
  classPopover: string;
  classPopoverButton: string;
  classPopoverBody: string;
  classPopoverContent: string;
  classPopoverContentItem: string;
  classColumns: string;
  classColumnsCol: string;
  classColumnsColAuto: string;
  classGroupEccImg: string;
  classGroupShopImg: string;
  classGroupShopName: string;
  classGroupShopIcon: string;
  classMarginBottom: string;
  classMarginLeft: string;
  classActive: string;
  labelGroupEccTelephone: string;
  labelGroupShopURL: string;
  labelGroupShopStatus: string;
  labelGroupShopPlan: string;
}

export interface IHeaderNavigation {
  faqInfo?: IHeaderFaqInfo,
  eccInfo?: IHeaderEccInfo,
  shopInfo?: IHeaderShopInfo,
}

export interface IHeaderFaqInfo {
  id: string;
  segments?: IHeaderDataItem[];
}

export interface IHeaderEccInfo {
  id: string;
  title?: string[];
  groupName?: string;
  name?: IHeaderDataItem;
  image?: { url: string; link: IHeaderDataItem; };
  telephone?: string;
  inquire?: IHeaderDataItem;
}

export interface IHeaderShopInfo {
  id: string;
  title?: string[];
  imageUrl?: string;
  shopName?: string;
  shopNameUrl?: string;
  shopStatus?: { title: string[], link: IHeaderDataItem };
  shopPlan?: { title: string[], link: IHeaderDataItem };
  shopUrl?: IHeaderDataItem;
  segments?: IHeaderDataItem[];
}

export interface IHeaderDataItem extends Omit<IDataItem, 'list'> {
  links?: IDataItem[];
}